import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { documentsSearchSchema } from 'routes/documents/-searchParams'
import { z } from 'zod'

/**
 * Dossiers page search params.
 *
 * The Dossiers page embeds the knowledge document viewer, whose URL-sync
 * (`useTabState`) reads/writes the document viewer params via
 * `useSearch({ from: '/dossiers/' })` / `useNavigate({ from: '/dossiers/' })`. We
 * therefore spread the full {@link documentsSearchSchema} so those params are
 * valid on this route, plus the global `siteId`.
 */
export const dossiersSearchParams = z.object({
  ...documentsSearchSchema.shape,
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
  /** Which top-level tab is active: subject lenses, process-data dossiers,
   * Asset Instances, the asset templates their fills are computed from, the
   * cross-kind review queue, the advanced-viewer, template monitors, or the
   * internal-only Fusion tools. */
  view: z
    .enum([
      'equipment',
      'instruments',
      'datastreams',
      'assets',
      'templates',
      'review',
      'fusion',
      'sentinels',
      'adv-viewer',
    ])
    .optional(),
  /** Currently selected dossier entry id (`fim-claim-<uuid>`). */
  dossierId: z.string().optional(),
  /** Currently selected Asset Instance entry id
   * (`fim-asset-instance-<subject_uid>`). */
  assetId: z.string().optional(),
  /** One-shot Assets-tab filter seed: show instances of this asset type (a
   * template's `applies_to_type`); consumed into the Filters state on arrival. */
  assetType: z.string().optional(),
  /** Currently selected sentinel entry id (`fim-sentinel-<site>-<asset>`). */
  sentinelId: z.string().optional(),
  /** Currently selected asset-template entry id
   * (`fim-asset-template-<template_id>`). */
  templateId: z.string().optional(),
  /** Currently selected subject entry id (`fim-subject-<subject_uid>`); shared by
   * the Equipment and Instruments lenses. */
  subjectId: z.string().optional(),
  /** One-shot seed for the Datastreams list search (a pivot from an instrument's
   * datastream row into its dossier). */
  dossierSearch: z.string().optional(),
})

export type DossiersSearchParams = z.infer<typeof dossiersSearchParams>

export const createDossiersSearch = (siteId: string | undefined) => ({
  [SEARCH_PARAM_SITE_ID]: siteId || undefined,
})
